import { Component, Inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { PromptTrackingService } from '../../services/prompt-tracking.service';
import { PromptResearchService, PromptResearch } from '../../services/prompt-research.service';

export interface PromptTrackingSourceDialogData {
  projectId: string;
  projectDetails?: {
    brand?: string;
    category?: string;
    industry?: string;
    country?: string;
    projectName?: string;
  };
}

export type PromptSourceType = 'generate' | 'upload' | 'select';

export interface PromptTrackingSourceResult {
  source: PromptSourceType;
  prompts?: string[];
  research_id?: string;
}

@Component({
  selector: 'app-prompt-tracking-source-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    MatAngularModule,
  ],
  templateUrl: './prompt-tracking-source-dialog.component.html',
  styleUrl: './prompt-tracking-source-dialog.component.scss',
})
export class PromptTrackingSourceDialogComponent {
  promptSource: PromptSourceType = 'generate';
  uploadPrompts: string[] = [];
  uploadErrors: string[] = [];
  validatingUpload = false;
  researchList: PromptResearch[] = [];
  selectedResearchId: string | null = null;
  loadingResearchList = false;

  constructor(
    public dialogRef: MatDialogRef<PromptTrackingSourceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PromptTrackingSourceDialogData,
    private promptTrackingService: PromptTrackingService,
    private promptResearchService: PromptResearchService,
    private cdr: ChangeDetectorRef
  ) {}

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onStart(): void {
    if (this.promptSource === 'generate') {
      this.dialogRef.close({ source: 'generate' } as PromptTrackingSourceResult);
      return;
    }
    if (this.promptSource === 'upload' && this.uploadPrompts.length > 0) {
      this.dialogRef.close({
        source: 'upload',
        prompts: this.uploadPrompts,
      } as PromptTrackingSourceResult);
      return;
    }
    if (this.promptSource === 'select' && this.selectedResearchId) {
      this.dialogRef.close({
        source: 'select',
        research_id: this.selectedResearchId,
      } as PromptTrackingSourceResult);
      return;
    }
  }

  get canStart(): boolean {
    if (this.promptSource === 'generate') return true;
    if (this.promptSource === 'upload') return this.uploadPrompts.length > 0;
    if (this.promptSource === 'select') return !!this.selectedResearchId;
    return false;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    this.uploadPrompts = [];
    this.uploadErrors = [];
    this.validatingUpload = true;
    this.cdr.detectChanges();
    this.promptTrackingService.validateUploadPrompts(file).subscribe({
      next: (r) => {
        this.uploadPrompts = r.prompts || [];
        this.uploadErrors = r.errors || [];
        this.validatingUpload = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.uploadErrors = [err.error?.detail || 'Validation failed'];
        this.uploadPrompts = [];
        this.validatingUpload = false;
        this.cdr.detectChanges();
      },
    });
    input.value = '';
  }

  loadResearchList(): void {
    if (!this.data.projectId) return;
    this.loadingResearchList = true;
    this.researchList = [];
    this.selectedResearchId = null;
    this.promptResearchService.getResearchList(this.data.projectId).subscribe({
      next: (r) => {
        this.researchList = r.researches || [];
        this.loadingResearchList = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingResearchList = false;
        this.cdr.detectChanges();
      },
    });
  }

  onSourceChange(): void {
    if (this.promptSource === 'select') this.loadResearchList();
    this.cdr.detectChanges();
  }

  /** Run change detection when user selects a research so canStart updates and Start button enables. */
  onResearchSelectionChange(): void {
    this.cdr.detectChanges();
  }
}
